import React, { useState, useEffect } from 'react';
import { Search, RotateCcw, X, CheckCircle, ChevronLeft, ChevronRight, Info } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api.js';

export default function AsignarIndiceModal({ item, onClose, onAsignado }) {
  const FILTROS_VACIOS = { 
    apellido_paterno: '', 
    apellido_materno: '', 
    primer_nombre: '',
    foja: '',
    numero: '',
    anio: '',
    tipo_registro: ''
  };
  
  const [filtros, setFiltros] = useState(FILTROS_VACIOS);
  const [resultados, setResultados] = useState(null);
  const [meta, setMeta] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [asignando, setAsignando] = useState(false);

  // Pre-poblar filtros desde el ítem y buscar automáticamente
  useEffect(() => {
    if (item) {
      const iniciales = {
        ...FILTROS_VACIOS,
        foja: item.foja || '',
        numero: item.numero || '',
        anio: item.anio || ''
      };
      setFiltros(iniciales);
      
      // Si tenemos datos mínimos, disparamos búsqueda inicial
      if (item.foja && item.numero) {
        ejecutarBusqueda(1, iniciales);
      }
    }
  }, [item]);

  const buscar = async (page = 1) => {
    const hayFiltro = Object.values(filtros).some((v) => v && v.toString().trim() !== '');
    if (!hayFiltro) {
      toast.error('Ingresa al menos un criterio de búsqueda.');
      return;
    }
    await ejecutarBusqueda(page, filtros);
  };

  const ejecutarBusqueda = async (page, customFiltros) => {
    setCargando(true);
    try {
      const { data } = await api.get('/buscador/propiedades', { 
        params: { ...customFiltros, page, per_page: 12 } 
      });
      setResultados(data.data);
      setMeta({
        current_page: data.current_page,
        last_page: data.last_page,
        total: data.total,
        from: data.from,
        to: data.to
      });
    } catch (err) {
      toast.error('Error al buscar índices.');
    } finally {
      setCargando(false);
    }
  };

  const asignar = async (indiceId) => {
    setAsignando(true);
    try {
      const { data } = await api.patch(`/solicitudes/items/${item.id}/asignar-indice`, {
        registro_indice_id: indiceId
      });
      toast.success('Registro asignado correctamente');
      onAsignado(); // Refrescar la tabla padre
    } catch (err) {
      toast.error('Error al asignar el registro.');
    } finally {
      setAsignando(false);
    }
  };

  return (
    <div className="modal-overlay animate-fade-in">
      <div className="glass-card flex flex-col" style={{ width: '100%', maxWidth: '950px', margin: '1rem', padding: '2rem', maxHeight: '95vh' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
          <div>
            <h3 style={{ margin: 0, color: 'var(--primary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Search size={20} /> Asignar Registro a Ítem
            </h3>
            <p className="text-muted" style={{ margin: '0.25rem 0 0', fontSize: '0.9rem' }}>
              Localice el registro histórico para vincularlo a esta solicitud.
            </p>
          </div>
          <button className="btn btn-secondary btn-sm" onClick={onClose} style={{ padding: '0.5rem', borderRadius: '50%' }}>
            <X size={20} />
          </button>
        </div>

        {/* Banner de Contexto del Trámite */}
        <div style={{ 
          background: 'var(--info-bg)', border: '1px solid #bfdbfe', borderRadius: '12px', 
          padding: '0.75rem 1rem', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '12px' 
        }}>
          <Info size={18} className="text-info" />
          <div style={{ fontSize: '0.85rem' }}>
            <span className="text-muted">Solicitado por cliente:</span>{' '}
            <strong className="text-primary">{item?.tipo_documento}</strong>{' '}
            {item?.foja && <span style={{ marginLeft: '8px' }}>Fojas: <strong>{item.foja}</strong></span>}
            {item?.numero && <span style={{ marginLeft: '8px' }}>N°: <strong>{item.numero}</strong></span>}
            {item?.anio && <span style={{ marginLeft: '8px' }}>Año: <strong>{item.anio}</strong></span>}
          </div>
        </div>

        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '1rem',
          marginBottom: '1.5rem', background: '#f8fafc', border: '1px solid var(--border-color)', padding: '1.25rem', borderRadius: '12px'
        }}>
          <div className="form-group" style={{ margin: 0 }}>
            <label>Tipo de Registro</label>
            <select value={filtros.tipo_registro} onChange={e => setFiltros(f => ({ ...f, tipo_registro: e.target.value }))}>
              <option value="">Cualquiera</option>
              <option value="propiedad">Propiedad</option>
              <option value="hipotecas">Hipotecas</option>
              <option value="prohibiciones">Prohibiciones</option>
            </select>
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label>Fojas</label>
            <input value={filtros.foja} onChange={e => setFiltros(f => ({ ...f, foja: e.target.value }))} placeholder="Ej: 120" />
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label>Número</label>
            <input value={filtros.numero} onChange={e => setFiltros(f => ({ ...f, numero: e.target.value }))} placeholder="Ej: 450" />
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label>Año</label>
            <input type="number" value={filtros.anio} onChange={e => setFiltros(f => ({ ...f, anio: e.target.value }))} placeholder="Ej: 2023" />
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label>Apellido Paterno</label>
            <input value={filtros.apellido_paterno} onChange={e => setFiltros(f => ({ ...f, apellido_paterno: e.target.value }))} />
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label>Nombre</label>
            <input value={filtros.primer_nombre} onChange={e => setFiltros(f => ({ ...f, primer_nombre: e.target.value }))} />
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end' }}>
            <button className="btn btn-primary btn-sm w-full" onClick={() => buscar(1)} disabled={cargando} style={{ height: '42px', flex: 1 }}>
              {cargando ? <span className="spinner" /> : <Search size={16} />} Buscar
            </button>
            <button className="btn btn-secondary btn-sm" onClick={() => setFiltros(FILTROS_VACIOS)} style={{ height: '42px', padding: '0 0.75rem' }} title="Limpiar filtros">
              <RotateCcw size={16} />
            </button>
          </div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', marginBottom: '1rem' }} className="table-wrapper">
          {resultados && resultados.length > 0 ? (
            <table className="animate-fade-in">
              <thead>
                <tr>
                  <th>Intervinientes</th><th>Actuación</th><th>Año</th><th>Acción</th>
                </tr>
              </thead>
              <tbody>
                {resultados.map(reg => (
                  <tr key={reg.id}>
                    <td>
                      {reg.intervinientes && reg.intervinientes.length > 0 ? (
                          reg.intervinientes.map((inv, idx) => (
                              <div key={idx} style={{ paddingBottom: idx !== reg.intervinientes.length - 1 ? '4px' : '0', lineHeight: '1.2' }}>
                                <strong style={{ fontSize: '0.85rem' }}>
                                  {[inv.nombres, inv.apellido_paterno, inv.apellido_materno].filter(Boolean).join(' ')}
                                </strong>
                                <span style={{ fontSize: '0.7rem', marginLeft: '6px', opacity: 0.6, textTransform: 'uppercase' }}>({inv.rol})</span>
                              </div>
                          ))
                      ) : (
                          <span className="text-muted">—</span>
                      )}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <span className={`badge ${
                        reg.tipo_registro === 'propiedad' ? 'badge-copper' : 
                        reg.tipo_registro === 'hipotecas' ? 'badge-info' : 
                        'badge-warning'
                      }`} style={{ textTransform: 'capitalize' }}>
                        {reg.tipo_registro}
                      </span>
                    </td>
                    <td style={{ fontWeight: 600, color: 'var(--primary)' }}>{reg.anio || '—'}</td>
                    <td>
                      <button 
                        className="btn btn-primary btn-sm" 
                        onClick={() => asignar(reg.id)}
                        disabled={asignando}
                        style={{ padding: '0.4rem 0.8rem', minWidth: '110px' }}
                      >
                        {asignando ? <span className="spinner" style={{ width: '14px', height: '14px' }} /> : <><CheckCircle size={14}/> Seleccionar</>}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : resultados && (
            <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
              <Info size={32} style={{ opacity: 0.2, marginBottom: '0.5rem' }} />
              <p>No se encontraron registros de índice para estos criterios.</p>
            </div>
          )}
        </div>

        {/* Paginación */}
        {meta && meta.last_page > 1 && (
          <div style={{ 
            display: 'flex', justifyContent: 'space-between', alignItems: 'center', 
            paddingTop: '1rem', borderTop: '1px solid var(--border-color)' 
          }}>
            <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
              Mostrando {meta.from}-{meta.to} de <strong>{meta.total}</strong> resultados
            </span>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button 
                className="btn btn-secondary btn-sm" 
                disabled={meta.current_page === 1 || cargando}
                onClick={() => buscar(meta.current_page - 1)}
                style={{ padding: '0.4rem' }}
              >
                <ChevronLeft size={16} />
              </button>
              <div style={{ display: 'flex', alignItems: 'center', px: '0.5rem', fontSize: '0.85rem', fontWeight: 600 }}>
                Página {meta.current_page} de {meta.last_page}
              </div>
              <button 
                className="btn btn-secondary btn-sm" 
                disabled={meta.current_page === meta.last_page || cargando}
                onClick={() => buscar(meta.current_page + 1)}
                style={{ padding: '0.4rem' }}
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
